/**
 * Date normalization for irrigation time frame constraints.
 *
 * Extracted from TimeFrameEdit so the save-time behavior can be unit tested:
 * an empty end date must stay empty ("until further notice") rather than being
 * frozen to the season's harvest date.
 */

const dayjs = require('../../../utils/dayjsConfig');

// What the date pickers hold in component state.
const PICKER_DATE_FORMAT = 'DD MMM YYYY';
// What gets persisted on the field's irrigation_system.
const STORED_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Build the active-period keys for a time frame about to be saved.
 *
 * An absent `active_period_end_date` means the constraint is open-ended: it
 * applies from its start date onward with no upper bound. The key is omitted
 * rather than filled from the season end, because the backend resolves a
 * missing end date against the *current* crop_data on every planner run - so
 * the constraint keeps applying when the harvest moves or a new crop cycle
 * starts on the field.
 *
 * @param {string|null} startDate - Picker value in PICKER_DATE_FORMAT.
 * @param {string|null} endDate - Picker value in PICKER_DATE_FORMAT, or empty
 *   for open-ended.
 * @param {object|string|null} sowDate - Fallback start, as a dayjs or ISO date.
 * @returns {{active_period_start_date?: string, active_period_end_date?: string}}
 */
const buildActivePeriod = (startDate, endDate, sowDate) => {
  const activePeriod = {};

  // The picker state and the sow-date fallback use different formats, so each
  // is parsed with its own rather than relying on Date fallback parsing.
  // Neither branch may pass undefined to dayjs() - that silently yields today.
  let start = null;
  if (startDate) {
    start = dayjs(startDate, PICKER_DATE_FORMAT);
  } else if (sowDate) {
    start = dayjs(sowDate);
  }
  if (start?.isValid()) {
    activePeriod.active_period_start_date = start.format(STORED_DATE_FORMAT);
  }

  if (endDate) {
    const end = dayjs(endDate, PICKER_DATE_FORMAT);
    if (end.isValid()) {
      activePeriod.active_period_end_date = end.format(STORED_DATE_FORMAT);
    }
  }

  return activePeriod;
};

module.exports = {
  buildActivePeriod,
  PICKER_DATE_FORMAT,
  STORED_DATE_FORMAT,
};
